import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  School as SchoolIcon,
  Security as SecurityIcon,
  Backup as BackupIcon,
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Storage as StorageIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Warning as WarningIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { motion } from 'framer-motion';

// CSS styles for animations
const styles = `
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .rotating {
    animation: rotate 1s linear infinite;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

const SystemSettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingSetting, setEditingSetting] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Data Management States
  const [factoryResetDialog, setFactoryResetDialog] = useState(false);
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [processingAction, setProcessingAction] = useState(false);
  
  // School Settings
  const [schoolSettings, setSchoolSettings] = useState({
    school_name: 'EMIS School',
    school_address: '123 Education Street',
    school_phone: '+1-234-567-8900',
    school_email: 'info@emisschool.edu',
    school_website: 'www.emisschool.edu',
    academic_year_start: '2024-04-01',
    academic_year_end: '2025-03-31',
    principal_name: 'Dr. John Smith',
    vice_principal_name: 'Ms. Jane Doe',
    school_logo: '',
    school_motto: 'Excellence in Education',
    established_year: '1995',
    affiliation: 'State Board of Education'
  });
  
  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    timezone: 'Asia/Kathmandu',
    date_format: 'YYYY-MM-DD',
    currency: 'NPR',
    language: 'en',
    session_timeout: '30',
    max_file_size: '10',
    backup_frequency: 'daily',
    maintenance_mode: false,
    debug_mode: false,
    auto_backup: true
  });
  
  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications: true,
    sms_notifications: false,
    push_notifications: true,
    fee_reminders: true,
    exam_alerts: true,
    attendance_alerts: true,
    admission_notifications: true,
    system_alerts: true
  });
  
  // Email Settings
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    smtp_encryption: 'tls',
    from_email: 'noreply@emisschool.edu',
    from_name: 'EMIS School'
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/settings');
      if (response.data.success) {
        const settings = response.data.data;
        setSchoolSettings(settings.school || schoolSettings);
        setSystemSettings(settings.system || systemSettings);
        setNotificationSettings(settings.notifications || notificationSettings);
        setEmailSettings(settings.email || emailSettings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (category, settings) => {
    try {
      setLoading(true);
      console.log('Saving settings:', { category, settings });
      
      const response = await axios.put('/api/settings', {
        category,
        settings
      });
      
      console.log('Settings response:', response.data);
      
      if (response.data.success) {
        enqueueSnackbar('Settings saved successfully', { variant: 'success' });
        // Refresh settings from database
        await fetchSettings();
      } else {
        enqueueSnackbar(response.data.message || 'Failed to save settings', { variant: 'error' });
      }
    } catch (error) {
      console.error('Save settings error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save settings';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleBackup = async () => {
    try {
      const response = await axios.post('/api/settings/backup');
      if (response.data.success) {
        enqueueSnackbar('Backup created successfully', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to create backup', { variant: 'error' });
    }
  };

  const handleTestEmail = async () => {
    try {
      const response = await axios.post('/api/settings/test-email', emailSettings);
      if (response.data.success) {
        enqueueSnackbar('Test email sent successfully', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to send test email', { variant: 'error' });
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        enqueueSnackbar('Logo file size must be less than 5MB', { variant: 'error' });
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        enqueueSnackbar('Please select an image file', { variant: 'error' });
        return;
      }
      
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      enqueueSnackbar('Please select a logo file first', { variant: 'warning' });
      return;
    }

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await axios.post('/api/settings/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const updatedSchoolSettings = {
          ...schoolSettings,
          school_logo: response.data.data.logoPath
        };
        
        // Update local state
        setSchoolSettings(updatedSchoolSettings);
        
        // Save to database immediately
        try {
          const saveResponse = await axios.put('/api/settings', {
            category: 'school',
            settings: updatedSchoolSettings
          });
          
          if (saveResponse.data.success) {
            enqueueSnackbar('Logo uploaded and saved successfully', { variant: 'success' });
            // Refresh settings from database
            await fetchSettings();
          } else {
            enqueueSnackbar('Logo uploaded but failed to save to database', { variant: 'warning' });
          }
        } catch (saveError) {
          console.error('Error saving logo to database:', saveError);
          enqueueSnackbar('Logo uploaded but failed to save to database', { variant: 'warning' });
        }
        
        setLogoFile(null);
        setLogoPreview(null);
        // Reset file input
        const fileInput = document.getElementById('logo-upload-input');
        if (fileInput) fileInput.value = '';
      }
    } catch (error) {
      console.error('Logo upload error:', error);
      enqueueSnackbar('Failed to upload logo', { variant: 'error' });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    const fileInput = document.getElementById('logo-upload-input');
    if (fileInput) fileInput.value = '';
  };

  // Data Management Functions
  const handleFactoryReset = async () => {
    try {
      setProcessingAction(true);
      const response = await axios.post('/api/settings/factory-reset', {
        confirmation: 'FACTORY_RESET_CONFIRMED'
      });
      
      if (response.data.success) {
        enqueueSnackbar('Factory reset completed successfully', { variant: 'success' });
        setFactoryResetDialog(false);
        // Refresh settings after reset
        await fetchSettings();
      } else {
        enqueueSnackbar(response.data.message || 'Factory reset failed', { variant: 'error' });
      }
    } catch (error) {
      console.error('Factory reset error:', error);
      enqueueSnackbar(error.response?.data?.message || 'Factory reset failed', { variant: 'error' });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDownloadSchema = async () => {
    try {
      setProcessingAction(true);
      const response = await axios.get('/api/settings/download-schema', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `emis-schema-${new Date().toISOString().split('T')[0]}.sql`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('Schema downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Download schema error:', error);
      enqueueSnackbar('Failed to download schema', { variant: 'error' });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleDownloadBackup = async () => {
    try {
      setProcessingAction(true);
      const response = await axios.get('/api/settings/download-backup', {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `emis-backup-${new Date().toISOString().split('T')[0]}.sql`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      enqueueSnackbar('Backup downloaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Download backup error:', error);
      enqueueSnackbar('Failed to download backup', { variant: 'error' });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRestoreFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith('.sql')) {
        enqueueSnackbar('Please select a .sql file', { variant: 'error' });
        return;
      }
      
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        enqueueSnackbar('File size must be less than 50MB', { variant: 'error' });
        return;
      }
      
      setRestoreFile(file);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) {
      enqueueSnackbar('Please select a backup file', { variant: 'warning' });
      return;
    }

    try {
      setProcessingAction(true);
      const formData = new FormData();
      formData.append('backup', restoreFile);

      const response = await axios.post('/api/settings/restore-backup', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        enqueueSnackbar('Database restored successfully', { variant: 'success' });
        setRestoreDialog(false);
        setRestoreFile(null);
        // Refresh settings after restore
        await fetchSettings();
      } else {
        enqueueSnackbar(response.data.message || 'Restore failed', { variant: 'error' });
      }
    } catch (error) {
      console.error('Restore error:', error);
      enqueueSnackbar(error.response?.data?.message || 'Restore failed', { variant: 'error' });
    } finally {
      setProcessingAction(false);
    }
  };

  const renderSchoolSettings = () => (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">School Information</Typography>
        <Button
          variant="contained"
          startIcon={loading ? <RefreshIcon className="rotating" /> : <SaveIcon />}
          onClick={() => handleSaveSettings('school', schoolSettings)}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="School Name"
            value={schoolSettings.school_name}
            onChange={(e) => setSchoolSettings({ ...schoolSettings, school_name: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Principal Name"
            value={schoolSettings.principal_name}
            onChange={(e) => setSchoolSettings({ ...schoolSettings, principal_name: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="School Phone"
            value={schoolSettings.school_phone}
            onChange={(e) => setSchoolSettings({ ...schoolSettings, school_phone: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="School Email"
            value={schoolSettings.school_email}
            onChange={(e) => setSchoolSettings({ ...schoolSettings, school_email: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="School Address"
            multiline
            rows={3}
            value={schoolSettings.school_address}
            onChange={(e) => setSchoolSettings({ ...schoolSettings, school_address: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Academic Year Start"
            type="date"
            value={schoolSettings.academic_year_start}
            onChange={(e) => setSchoolSettings({ ...schoolSettings, academic_year_start: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Academic Year End"
            type="date"
            value={schoolSettings.academic_year_end}
            onChange={(e) => setSchoolSettings({ ...schoolSettings, academic_year_end: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="School Website"
            value={schoolSettings.school_website}
            onChange={(e) => setSchoolSettings({ ...schoolSettings, school_website: e.target.value })}
          />
        </Grid>
        
        {/* School Logo Upload Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>School Logo</Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Current Logo Display */}
            {schoolSettings.school_logo && !logoPreview && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Current Logo:</Typography>
                <Box
                  component="img"
                  src={schoolSettings.school_logo.startsWith('http') ? schoolSettings.school_logo : `http://localhost:3000${schoolSettings.school_logo}`}
                  alt="School Logo"
                  sx={{
                    maxWidth: 200,
                    maxHeight: 100,
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    p: 1
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            )}
            
            {/* Logo Preview */}
            {logoPreview && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Logo Preview:</Typography>
                <Box
                  component="img"
                  src={logoPreview}
                  alt="Logo Preview"
                  sx={{
                    maxWidth: 200,
                    maxHeight: 100,
                    objectFit: 'contain',
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    p: 1
                  }}
                />
              </Box>
            )}
            
            {/* Logo Upload Controls */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="logo-upload-input"
                type="file"
                onChange={handleLogoChange}
              />
              <label htmlFor="logo-upload-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<SchoolIcon />}
                >
                  Select Logo
                </Button>
              </label>
              
              {logoFile && (
                <>
                  <Button
                    variant="contained"
                    onClick={handleLogoUpload}
                    disabled={uploadingLogo}
                    startIcon={uploadingLogo ? <RefreshIcon className="rotating" /> : <SaveIcon />}
                  >
                    {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveLogo}
                    disabled={uploadingLogo}
                  >
                    Remove
                  </Button>
                </>
              )}
            </Box>
            
            <Typography variant="caption" color="text.secondary">
              Supported formats: JPG, PNG, GIF. Maximum size: 5MB. Recommended dimensions: 200x100px
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Established Year"
            value={schoolSettings.established_year}
            onChange={(e) => setSchoolSettings({ ...schoolSettings, established_year: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="School Motto"
            value={schoolSettings.school_motto}
            onChange={(e) => setSchoolSettings({ ...schoolSettings, school_motto: e.target.value })}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  const renderSystemSettings = () => (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">System Configuration</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<BackupIcon />}
            onClick={handleBackup}
            sx={{ mr: 2 }}
          >
            Create Backup
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => handleSaveSettings('system', systemSettings)}
          >
            Save Changes
          </Button>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Timezone</InputLabel>
            <Select
              value={systemSettings.timezone}
              onChange={(e) => setSystemSettings({ ...systemSettings, timezone: e.target.value })}
            >
              <MenuItem value="Asia/Kathmandu">Asia/Kathmandu</MenuItem>
              <MenuItem value="UTC">UTC</MenuItem>
              <MenuItem value="America/New_York">America/New_York</MenuItem>
              <MenuItem value="Europe/London">Europe/London</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Date Format</InputLabel>
            <Select
              value={systemSettings.date_format}
              onChange={(e) => setSystemSettings({ ...systemSettings, date_format: e.target.value })}
            >
              <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
              <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
              <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Currency</InputLabel>
            <Select
              value={systemSettings.currency}
              onChange={(e) => setSystemSettings({ ...systemSettings, currency: e.target.value })}
            >
              <MenuItem value="NPR">NPR - Nepalese Rupee</MenuItem>
              <MenuItem value="USD">USD - US Dollar</MenuItem>
              <MenuItem value="EUR">EUR - Euro</MenuItem>
              <MenuItem value="INR">INR - Indian Rupee</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Session Timeout (minutes)"
            type="number"
            value={systemSettings.session_timeout}
            onChange={(e) => setSystemSettings({ ...systemSettings, session_timeout: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Max File Size (MB)"
            type="number"
            value={systemSettings.max_file_size}
            onChange={(e) => setSystemSettings({ ...systemSettings, max_file_size: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Backup Frequency</InputLabel>
            <Select
              value={systemSettings.backup_frequency}
              onChange={(e) => setSystemSettings({ ...systemSettings, backup_frequency: e.target.value })}
            >
              <MenuItem value="daily">Daily</MenuItem>
              <MenuItem value="weekly">Weekly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={systemSettings.maintenance_mode}
                  onChange={(e) => setSystemSettings({ ...systemSettings, maintenance_mode: e.target.checked })}
                />
              }
              label="Maintenance Mode"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={systemSettings.debug_mode}
                  onChange={(e) => setSystemSettings({ ...systemSettings, debug_mode: e.target.checked })}
                />
              }
              label="Debug Mode"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={systemSettings.auto_backup}
                  onChange={(e) => setSystemSettings({ ...systemSettings, auto_backup: e.target.checked })}
                />
              }
              label="Auto Backup"
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderNotificationSettings = () => (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">Notification Preferences</Typography>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={() => handleSaveSettings('notifications', notificationSettings)}
        >
          Save Changes
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>General Notifications</Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.email_notifications}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, email_notifications: e.target.checked })}
                />
              }
              label="Email Notifications"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.sms_notifications}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, sms_notifications: e.target.checked })}
                />
              }
              label="SMS Notifications"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.push_notifications}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, push_notifications: e.target.checked })}
                />
              }
              label="Push Notifications"
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Specific Alerts</Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.fee_reminders}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, fee_reminders: e.target.checked })}
                />
              }
              label="Fee Payment Reminders"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.exam_alerts}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, exam_alerts: e.target.checked })}
                />
              }
              label="Exam Alerts"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.attendance_alerts}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, attendance_alerts: e.target.checked })}
                />
              }
              label="Attendance Alerts"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.admission_notifications}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, admission_notifications: e.target.checked })}
                />
              }
              label="Admission Notifications"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={notificationSettings.system_alerts}
                  onChange={(e) => setNotificationSettings({ ...notificationSettings, system_alerts: e.target.checked })}
                />
              }
              label="System Alerts"
            />
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderEmailSettings = () => (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">Email Configuration</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<EmailIcon />}
            onClick={handleTestEmail}
            sx={{ mr: 2 }}
          >
            Test Email
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => handleSaveSettings('email', emailSettings)}
          >
            Save Changes
          </Button>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="SMTP Host"
            value={emailSettings.smtp_host}
            onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="SMTP Port"
            value={emailSettings.smtp_port}
            onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="SMTP Username"
            value={emailSettings.smtp_username}
            onChange={(e) => setEmailSettings({ ...emailSettings, smtp_username: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="SMTP Password"
            type="password"
            value={emailSettings.smtp_password}
            onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>SMTP Encryption</InputLabel>
            <Select
              value={emailSettings.smtp_encryption}
              onChange={(e) => setEmailSettings({ ...emailSettings, smtp_encryption: e.target.value })}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="tls">TLS</MenuItem>
              <MenuItem value="ssl">SSL</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="From Email"
            value={emailSettings.from_email}
            onChange={(e) => setEmailSettings({ ...emailSettings, from_email: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="From Name"
            value={emailSettings.from_name}
            onChange={(e) => setEmailSettings({ ...emailSettings, from_name: e.target.value })}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  const renderDataManagement = () => (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h6">Data Management</Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your database with backup, restore, and reset operations
        </Typography>
      </Box>
      
      <Grid container spacing={3}>
        {/* Backup Operations */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <DownloadIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Download Operations</Typography>
              </Box>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={processingAction ? <CircularProgress size={20} /> : <DownloadIcon />}
                  onClick={handleDownloadSchema}
                  disabled={processingAction}
                  fullWidth
                >
                  Download Schema
                </Button>
                
                <Button
                  variant="contained"
                  startIcon={processingAction ? <CircularProgress size={20} /> : <BackupIcon />}
                  onClick={handleDownloadBackup}
                  disabled={processingAction}
                  fullWidth
                >
                  Download Full Backup
                </Button>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Download database structure or complete backup with data
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Restore Operations */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <UploadIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">Restore Operations</Typography>
              </Box>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => setRestoreDialog(true)}
                  fullWidth
                >
                  Restore from Backup
                </Button>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Restore database from a previously downloaded backup file
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Factory Reset */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ border: '2px solid', borderColor: 'error.main' }}>
            <CardContent>
              <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                <DeleteForeverIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6" color="error">Danger Zone</Typography>
              </Box>
              
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Warning:</strong> Factory reset will permanently delete all data except system settings and admin user. 
                  This action cannot be undone. Please create a backup before proceeding.
                </Typography>
              </Alert>
              
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteForeverIcon />}
                onClick={() => setFactoryResetDialog(true)}
                fullWidth
              >
                Factory Reset - Clear All Data
              </Button>
              
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                This will reset the application to a fresh state while preserving system configuration and admin user
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Factory Reset Confirmation Dialog */}
      <Dialog open={factoryResetDialog} onClose={() => setFactoryResetDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <WarningIcon color="error" sx={{ mr: 1 }} />
            Confirm Factory Reset
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you absolutely sure you want to perform a factory reset?
          </Typography>
          
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              This action will:
            </Typography>
            <ul>
              <li>Delete all student records</li>
              <li>Delete all teacher records</li>
              <li>Delete all class and subject data</li>
              <li>Delete all attendance records</li>
              <li>Delete all exam records</li>
              <li>Delete all fee records</li>
              <li>Reset all auto-increment counters</li>
            </ul>
            <Typography variant="body2">
              <strong>System settings, school information, and admin user will be preserved.</strong>
            </Typography>
          </Alert>
          
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. Consider creating a backup first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setFactoryResetDialog(false)} 
            disabled={processingAction}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFactoryReset}
            color="error"
            variant="contained"
            disabled={processingAction}
            startIcon={processingAction ? <CircularProgress size={20} /> : <DeleteForeverIcon />}
          >
            {processingAction ? 'Resetting...' : 'Reset Everything'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={restoreDialog} onClose={() => setRestoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <UploadIcon color="info" sx={{ mr: 1 }} />
            Restore Database
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Select a backup file to restore the database:
          </Typography>
          
          <Box sx={{ mb: 2 }}>
            <input
              accept=".sql"
              style={{ display: 'none' }}
              id="restore-file-input"
              type="file"
              onChange={handleRestoreFileChange}
            />
            <label htmlFor="restore-file-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                fullWidth
              >
                Select Backup File (.sql)
              </Button>
            </label>
          </Box>
          
          {restoreFile && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="success.main">
                Selected file: {restoreFile.name} ({(restoreFile.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            </Box>
          )}
          
          <Alert severity="warning">
            <Typography variant="body2">
              Restoring will overwrite existing data. This action cannot be undone.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setRestoreDialog(false);
              setRestoreFile(null);
            }} 
            disabled={processingAction}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRestore}
            variant="contained"
            disabled={processingAction || !restoreFile}
            startIcon={processingAction ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {processingAction ? 'Restoring...' : 'Restore Backup'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        System Settings
      </Typography>

      {/* Settings Categories */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<SchoolIcon />} label="School Info" />
          <Tab icon={<SettingsIcon />} label="System" />
          <Tab icon={<NotificationsIcon />} label="Notifications" />
          <Tab icon={<EmailIcon />} label="Email" />
          <Tab icon={<StorageIcon />} label="Data Management" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 && renderSchoolSettings()}
      {tabValue === 1 && renderSystemSettings()}
      {tabValue === 2 && renderNotificationSettings()}
      {tabValue === 3 && renderEmailSettings()}
      {tabValue === 4 && renderDataManagement()}
    </Box>
  );
};

export default SystemSettings;
